import {
  Div,
  lightTheme,
  NoticeBanner,
  SystemProblem,
  TOOLTIP_ID,
} from '@vgrubs/components';
import { useState } from 'react';
import {
  InPlaceActions,
  severityColor,
  SystemProblemModal,
  useSystemProblem,
} from '../../../SystemProblems';
import {
  MissingDataAction,
  missingDataGuide,
} from '../helpers/missingDataGuide';
import { useLocationAlerts } from '../hooks/useLocationAlerts';
import { Chip, ChipDot, ChipRow, ChipText } from './LocationProblems.styled';

type Props = {
  locationId: number | string;
  onAction: (action: Exclude<MissingDataAction, null>) => void;
  /** Opens this screen's services modal, for "Open SaaS settings". */
  onOpenServices: () => void;
};

/**
 * A problem's chip says what is wrong in a few words. The backend's titles
 * end in "at <location>", which this screen already says in its header.
 */
const problemChipLabel = (problem: SystemProblem) => {
  const suffix = problem.locationName ? ` at ${problem.locationName}` : '';
  return suffix && problem.title.endsWith(suffix)
    ? problem.title.slice(0, -suffix.length)
    : problem.title;
};

/**
 * Everything wrong with the location, as one alert like the debt banner
 * above it rather than a list: a chip per open system problem (most severe
 * first, as the backend orders them), then one per thing we do not have on
 * file. Hovering a chip says more; a problem opens the same detail as on the
 * Dashboard, resolved from there, and a missing item opens where it gets
 * added. Red when any problem is critical, yellow otherwise. Nothing at all
 * when there is nothing to fix.
 *
 * Admin and support only - the screen does not render it for anyone else.
 */
export const LocationProblems = ({
  locationId,
  onAction,
  onOpenServices,
}: Props) => {
  const { alerts } = useLocationAlerts({ locationId, enabled: true });
  const [problemId, setProblemId] = useState<number | null>(null);
  const { problem } = useSystemProblem(problemId);

  // The problem's detail closes first, or the services modal would open
  // under it.
  const inPlace: InPlaceActions = {
    services: () => {
      setProblemId(null);
      onOpenServices();
    },
  };

  const modal = (
    <SystemProblemModal
      isOpen={problemId !== null}
      problem={problem}
      onClose={() => setProblemId(null)}
      inPlace={inPlace}
    />
  );

  // Stays mounted while a resolved problem's detail is still open, even if
  // that was the last thing to fix.
  if (!alerts || alerts.total === 0) return modal;

  const critical = alerts.systemProblems.some(
    (row) => row.severity === 'critical'
  );

  return (
    <Div $m="0 0 32px" $mSM="0 0 20px">
      <NoticeBanner
        critical={critical}
        title={`${alerts.total} location ${
          alerts.total === 1 ? 'problem' : 'problems'
        }`}
      >
        <ChipRow>
          {alerts.systemProblems.map((row) => (
            <Chip
              key={row.id}
              type="button"
              $clickable
              onClick={() => setProblemId(row.id)}
              data-tooltip-id={TOOLTIP_ID}
              data-tooltip-content={row.description}
              data-tooltip-place="top"
            >
              <ChipDot $color={severityColor(row.severity)} />
              <ChipText>{problemChipLabel(row)}</ChipText>
            </Chip>
          ))}

          {alerts.missingData.map((item) => {
            const { label, hint, action } = missingDataGuide(item);
            return (
              <Chip
                key={item.type}
                type="button"
                $clickable={!!action}
                onClick={action ? () => onAction(action) : undefined}
                data-tooltip-id={TOOLTIP_ID}
                data-tooltip-content={hint || item.title}
                data-tooltip-place="top"
              >
                {/* Missing data is a warning, as the backend doc has it. */}
                <ChipDot $color={lightTheme.yellow} />
                <ChipText>{label}</ChipText>
              </Chip>
            );
          })}
        </ChipRow>
      </NoticeBanner>
      {modal}
    </Div>
  );
};
